import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { useProjects } from "@/api/hooks/useProjects";
import { useScenes } from "@/api/hooks/useScenes";
import { useShots } from "@/api/hooks/useShots";
import { useCharacters } from "@/api/hooks/useCharacters";
import { useLocations } from "@/api/hooks/useLocations";
import { useProps } from "@/api/hooks/useProps";
import { useCostumes } from "@/api/hooks/useCostumes";
import styles from "./CommandPalette.module.css";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

type ResultKind =
  | "project"
  | "scene"
  | "shot"
  | "character"
  | "location"
  | "prop"
  | "costume";

interface PaletteResult {
  /** Stable key for React lists; combines kind and id. */
  key: string;
  /** Section bucket label, e.g. "PROJECTS". */
  section: string;
  kind: ResultKind;
  /** Primary line — usually the entity name. */
  primary: string;
  /** Right-aligned subtle label, e.g. "scene · Wing Chun". */
  secondary?: string;
  /** Destination route for `useNavigate`. */
  to: string;
}

const SECTION_ORDER: Record<ResultKind, number> = {
  project: 0,
  scene: 1,
  shot: 2,
  character: 3,
  location: 4,
  prop: 5,
  costume: 6,
};

const SECTION_LABELS: Record<ResultKind, string> = {
  project: "PROJECTS",
  scene: "SCENES",
  shot: "SHOTS",
  character: "CHARACTERS",
  location: "LOCATIONS",
  prop: "PROPS",
  costume: "COSTUMES",
};

/** Cap results per section so a sprawling project doesn't drown the palette. */
const PER_SECTION_LIMIT = 10;

function ciIncludes(haystack: string | null | undefined, needle: string): boolean {
  if (!haystack) return false;
  return haystack.toLowerCase().includes(needle);
}

/**
 * Modal command palette opened with mod+K.
 *
 * V1 design choices (deliberately simple):
 *  - Substring (not subsequence) matching, case-insensitive. ~50–200 items
 *    per project; no debounce needed for a substring scan that small.
 *  - We render flat ungrouped — each item has its own row, with a section
 *    header inserted before the first item of each kind. Arrow keys index
 *    the FLAT list (skipping the headers), so j/k feel snappy.
 *  - Scope: scenes/characters/etc. are project-scoped via `useParams().id`;
 *    shots are scene-scoped via `useParams().sceneId`. When the user is on
 *    `/` (no project active), only projects appear in the empty-query state
 *    and only project matches when filtering.
 *
 * Project switcher (⌘P) reuses this same palette — see AppShell wiring.
 * A dedicated dropdown is a V2 follow-up.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const params = useParams<{ id?: string; sceneId?: string }>();
  const projectId = params.id ? Number(params.id) : 0;
  const sceneId = params.sceneId ? Number(params.sceneId) : 0;

  const projects = useProjects();
  const scenes = useScenes(projectId);
  const shots = useShots(sceneId);
  const characters = useCharacters(projectId);
  const locations = useLocations(projectId);
  const props = useProps(projectId);
  const costumes = useCostumes(projectId);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Reset query + selection each time the palette opens, and remember which
  // element opened us so we can restore focus on close (Modal does this for
  // its dialogs; we replicate the pattern here since we don't use <Modal>).
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      triggerRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // Defer focus so the input has actually rendered.
      const t = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
    // Closing: restore focus to whoever opened us, when the element is still
    // in the DOM. (E.g. a ⌘K topbar button.)
    const trigger = triggerRef.current;
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    }
    triggerRef.current = null;
    return undefined;
  }, [open]);

  // ---------------------------------------------------------------- matching

  const results = useMemo<PaletteResult[]>(() => {
    if (!open) return [];
    const q = query.trim().toLowerCase();
    const out: PaletteResult[] = [];

    // Projects — always available, even on the root route.
    if (projects.data) {
      const matches = projects.data.filter((p) =>
        q.length === 0 ? true : ciIncludes(p.name, q),
      );
      for (const p of matches.slice(0, PER_SECTION_LIMIT)) {
        out.push({
          key: `project-${p.id}`,
          section: SECTION_LABELS.project,
          kind: "project",
          primary: p.name,
          secondary: p.mode === "prompt_only" ? "prompt-only" : "full pipeline",
          to: `/projects/${p.id}`,
        });
      }
    }

    // Project-scoped buckets — only when a project is active.
    if (projectId > 0) {
      if (scenes.data) {
        const matches = scenes.data.filter((s) =>
          q.length === 0 ? true : ciIncludes(s.name, q),
        );
        for (const s of matches.slice(0, PER_SECTION_LIMIT)) {
          out.push({
            key: `scene-${s.id}`,
            section: SECTION_LABELS.scene,
            kind: "scene",
            primary: s.name,
            secondary: `scene ${s.ordinal + 1}`,
            to: `/projects/${projectId}/scenes/${s.id}`,
          });
        }
      }

      // Shots live one level deeper — only when a scene is also active.
      if (sceneId > 0 && shots.data) {
        const matches = shots.data.filter((sh) => {
          if (q.length === 0) return true;
          return ciIncludes(sh.shot_number, q) || ciIncludes(sh.beat_notes, q);
        });
        for (const sh of matches.slice(0, PER_SECTION_LIMIT)) {
          const beat = (sh.beat_notes ?? "").trim();
          out.push({
            key: `shot-${sh.id}`,
            section: SECTION_LABELS.shot,
            kind: "shot",
            primary: beat.length > 0 ? `Shot ${sh.shot_number} — ${beat}` : `Shot ${sh.shot_number}`,
            secondary: `${sh.duration_seconds}s`,
            // Pin the shot via the ?shot= URL param so the existing ShotCard
            // scroll-into-view + expand handling fires on navigation.
            to: `/projects/${projectId}/scenes/${sceneId}?shot=${sh.id}`,
          });
        }
      }

      if (characters.data) {
        const matches = characters.data.filter((c) =>
          q.length === 0 ? true : ciIncludes(c.name, q),
        );
        for (const c of matches.slice(0, PER_SECTION_LIMIT)) {
          out.push({
            key: `character-${c.id}`,
            section: SECTION_LABELS.character,
            kind: "character",
            primary: c.name,
            to: `/projects/${projectId}/characters/${c.id}`,
          });
        }
      }

      if (locations.data) {
        const matches = locations.data.filter((l) =>
          q.length === 0 ? true : ciIncludes(l.name, q),
        );
        for (const l of matches.slice(0, PER_SECTION_LIMIT)) {
          out.push({
            key: `location-${l.id}`,
            section: SECTION_LABELS.location,
            kind: "location",
            primary: l.name,
            to: `/projects/${projectId}/locations/${l.id}`,
          });
        }
      }

      if (props.data) {
        const matches = props.data.filter((p) =>
          q.length === 0 ? true : ciIncludes(p.name, q),
        );
        for (const p of matches.slice(0, PER_SECTION_LIMIT)) {
          out.push({
            key: `prop-${p.id}`,
            section: SECTION_LABELS.prop,
            kind: "prop",
            primary: p.name,
            to: `/projects/${projectId}/props/${p.id}`,
          });
        }
      }

      if (costumes.data) {
        const matches = costumes.data.filter((c) =>
          q.length === 0 ? true : ciIncludes(c.name, q),
        );
        for (const c of matches.slice(0, PER_SECTION_LIMIT)) {
          out.push({
            key: `costume-${c.id}`,
            section: SECTION_LABELS.costume,
            kind: "costume",
            primary: c.name,
            to: `/projects/${projectId}/costumes/${c.id}`,
          });
        }
      }
    }

    // Stable section order then preserve in-section ordering from the API.
    out.sort((a, b) => SECTION_ORDER[a.kind] - SECTION_ORDER[b.kind]);
    return out;
  }, [
    open,
    query,
    projectId,
    sceneId,
    projects.data,
    scenes.data,
    shots.data,
    characters.data,
    locations.data,
    props.data,
    costumes.data,
  ]);

  // Clamp activeIndex when results shrink (e.g. user types a more specific
  // query). Without this the highlight can point past the end of the array.
  useEffect(() => {
    if (activeIndex >= results.length) {
      setActiveIndex(results.length > 0 ? results.length - 1 : 0);
    }
  }, [results.length, activeIndex]);

  // Group results into rendered sections — collected here so we can show
  // headers above the first item of each kind.
  const renderGroups = useMemo(() => {
    const groups: Array<{ section: string; items: Array<{ result: PaletteResult; flatIndex: number }> }> = [];
    let lastSection: string | null = null;
    results.forEach((r, flatIndex) => {
      if (r.section !== lastSection) {
        groups.push({ section: r.section, items: [] });
        lastSection = r.section;
      }
      groups[groups.length - 1]!.items.push({ result: r, flatIndex });
    });
    return groups;
  }, [results]);

  // ---------------------------------------------------------------- actions

  const select = useCallback(
    (result: PaletteResult) => {
      onClose();
      navigate(result.to);
    },
    [navigate, onClose],
  );

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (results.length === 0) return;
      setActiveIndex((i) => (i + 1) % results.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (results.length === 0) return;
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const active = results[activeIndex];
      if (active) select(active);
      return;
    }
  };

  // Scroll the highlighted row into view as activeIndex changes via arrows.
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-flat-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={styles.backdrop}
          onMouseDown={(e) => {
            // Backdrop click closes; clicks inside the panel are stopped by the
            // panel handler below.
            if (e.target === e.currentTarget) onClose();
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1, ease: [0.2, 0, 0, 1] }}
        >
          <motion.div
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            onMouseDown={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.14, ease: [0.2, 0, 0, 1] }}
          >
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={onInputKeyDown}
          placeholder="Search projects, scenes, shots, characters…"
          aria-label="Search"
          aria-autocomplete="list"
          aria-controls="sf-palette-list"
          aria-activedescendant={
            results[activeIndex] ? `sf-palette-item-${results[activeIndex].key}` : undefined
          }
          autoComplete="off"
          spellCheck={false}
        />
        {results.length === 0 ? (
          <p className={styles.empty}>No results</p>
        ) : (
          <ul
            id="sf-palette-list"
            ref={listRef}
            className={styles.list}
            role="listbox"
            aria-label="Search results"
          >
            {renderGroups.map((group) => (
              <li key={group.section} role="presentation">
                <div className={styles.sectionLabel} role="presentation">
                  {group.section}
                </div>
                <ul role="presentation" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {group.items.map(({ result, flatIndex }) => {
                    const active = flatIndex === activeIndex;
                    return (
                      <li
                        key={result.key}
                        id={`sf-palette-item-${result.key}`}
                        role="option"
                        aria-selected={active}
                      >
                        <button
                          type="button"
                          data-flat-index={flatIndex}
                          className={`${styles.item} ${active ? styles.itemActive : ""}`}
                          // Use mousedown so the click fires before the input
                          // loses focus — otherwise the dialog's focus-restore
                          // can intercept the click on the underlying topbar.
                          onMouseDown={(e) => {
                            e.preventDefault();
                            select(result);
                          }}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                        >
                          <span className={styles.itemPrimary}>{result.primary}</span>
                          {result.secondary ? (
                            <span className={styles.itemSecondary}>{result.secondary}</span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
            <div className={styles.hintRow}>
              <span><span className={styles.hintKey}>↑↓</span> navigate</span>
              <span className={styles.hintDot}>·</span>
              <span><span className={styles.hintKey}>enter</span> open</span>
              <span className={styles.hintDot}>·</span>
              <span><span className={styles.hintKey}>esc</span> close</span>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
